import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { IndustryBackgroundForm } from '@/components/forms/profile/IndustryBackgroundForm';
import { IndustryInterestForm } from '@/components/forms/profile/IndustryInterestForm';
import { PersonalBackgroundForm } from '@/components/forms/profile/PersonalBackgroundForm';
import { PersonalBackgroundFormSchema } from '@/components/forms/schemas/PersonalBackgroundFormSchema';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/ui/use-toast';
import { useCreateZeeBackground } from '@/hooks/zee-background/useCreateZeeBackground';
import { useGetZeeBackground } from '@/hooks/zee-background/useGetZeeBackground';
import { useUpdateZeeBackground } from '@/hooks/zee-background/useUpdateZeeBackground';
import { useZeeStore } from '@/stores/useZeeStore';

type PersonalBackgroundFormValues = z.infer<
  typeof PersonalBackgroundFormSchema
>;

const BackgroundTab = ({
  setIsDirty,
}: {
  setIsDirty: (isDirty: boolean) => void;
}) => {
  const [industryInterests, setIndustryInterests] = useState<
    Record<string, number>
  >({});
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);

  const personalBackgroundForm = useForm<PersonalBackgroundFormValues>({
    resolver: zodResolver(PersonalBackgroundFormSchema),
    defaultValues: {},
  });

  const { zeeBackground, setZeeBackground } = useZeeStore();

  const {
    result: zeeBackgroundResult,
    isError: isZeeBackgroundError,
    isSuccess: isZeeBackgroundSuccess,
    isLoading: isZeeBackgroundLoading,
  } = useGetZeeBackground();
  const {
    mutate: createZeeBackground,
    result: createZeeBackgroundResult,
    isError: isCreateZeeBackgroundError,
    isSuccess: isCreateZeeBackgroundSuccess,
    isLoading: isCreateZeeBackgroundLoading,
  } = useCreateZeeBackground();
  const {
    mutate: updateZeeBackground,
    result: updateZeeBackgroundResult,
    isError: isUpdateZeeBackgroundError,
    isSuccess: isUpdateZeeBackgroundSuccess,
    isLoading: isUpdateZeeBackgroundLoading,
  } = useUpdateZeeBackground();

  const resetForms = useCallback(() => {
    personalBackgroundForm.reset({
      ...zeeBackground,
      affirmative_answers_explanation:
        zeeBackground?.affirmative_answers_explanation || '',
    });

    setSelectedIndustries([...(zeeBackground?.industry_background || [])]);
    setIndustryInterests({
      ...(zeeBackground?.industry_interest || {}),
    });
    setIsDirty(false);
  }, [zeeBackground, setIsDirty]);

  // Update isDirty state whenever form state or industry selections change
  useEffect(() => {
    const isIndustryBackgroundChanged =
      JSON.stringify(zeeBackground?.industry_background || []) !==
      JSON.stringify(selectedIndustries);
    const isIndustryInterestChanged =
      JSON.stringify(zeeBackground?.industry_interest || {}) !==
      JSON.stringify(industryInterests);

    setIsDirty(
      personalBackgroundForm.formState.isDirty ||
        isIndustryBackgroundChanged ||
        isIndustryInterestChanged
    );
  }, [
    personalBackgroundForm.formState.isDirty,
    selectedIndustries,
    industryInterests,
    zeeBackground?.industry_background,
    zeeBackground?.industry_interest,
    setIsDirty,
  ]);

  const handleSubmit = useCallback(async (): Promise<void> => {
    const isPersonalBackgroundFormValid =
      await personalBackgroundForm.trigger();

    if (isPersonalBackgroundFormValid) {
      const submitPayload = {
        ...personalBackgroundForm.getValues(),
        industry_background: selectedIndustries,
        industry_interest: industryInterests,
      };

      if (!zeeBackground) {
        createZeeBackground(submitPayload);
      } else {
        updateZeeBackground(submitPayload);
      }
    }
  }, [
    personalBackgroundForm,
    selectedIndustries,
    industryInterests,
    zeeBackground,
    createZeeBackground,
    updateZeeBackground,
  ]);

  const renderEditButtons = () => {
    return (
      <div className="flex w-full justify-end">
        <div className="flex gap-1">
          <Button size="sm" className="w-full md:w-fit" onClick={handleSubmit}>
            Save Changes
          </Button>
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (isZeeBackgroundError) {
      toast({
        title: 'Something went wrong',
        description: 'Please try again',
        variant: 'destructive',
      });
    }
  }, [isZeeBackgroundError]);

  useEffect(() => {
    if (isZeeBackgroundSuccess && zeeBackgroundResult?.data) {
      if (zeeBackgroundResult?.data) {
        setZeeBackground(zeeBackgroundResult?.data);
      }

      resetForms();
    }
  }, [
    zeeBackgroundResult,
    isZeeBackgroundSuccess,
    resetForms,
    setZeeBackground,
  ]);

  useEffect(() => {
    if (isCreateZeeBackgroundError || isUpdateZeeBackgroundError) {
      toast({
        title: 'Something went wrong',
        description: 'Please try again',
        variant: 'destructive',
      });
    }
  }, [isCreateZeeBackgroundError, isUpdateZeeBackgroundError]);

  useEffect(() => {
    if (isCreateZeeBackgroundSuccess || isUpdateZeeBackgroundSuccess) {
      if (createZeeBackgroundResult?.data) {
        setZeeBackground(createZeeBackgroundResult?.data);
      }

      if (updateZeeBackgroundResult?.data) {
        setZeeBackground(updateZeeBackgroundResult?.data);
      }

      resetForms();
      toast({ description: 'Changes saved successfully' });
    }
  }, [
    createZeeBackgroundResult,
    updateZeeBackgroundResult,
    isCreateZeeBackgroundSuccess,
    isUpdateZeeBackgroundSuccess,
    resetForms,
    setZeeBackground,
  ]);

  // no parent save registration; users save manually

  if (isZeeBackgroundLoading) {
    return (
      <div className="flex h-full w-full flex-1 items-center justify-center">
        <Loader2 className="size-4 animate-spin" />
      </div>
    );
  }

  return (
    <div className="relative space-y-1.5">
      {(isCreateZeeBackgroundLoading || isUpdateZeeBackgroundLoading) && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-gray-100/50 backdrop-blur-sm">
          <Loader2 className="size-4 animate-spin" />
        </div>
      )}

      <PersonalBackgroundForm form={personalBackgroundForm} />
      <IndustryBackgroundForm
        selectedIndustries={selectedIndustries}
        setSelectedIndustries={setSelectedIndustries}
      />
      <IndustryInterestForm
        industryInterests={industryInterests}
        setIndustryInterests={setIndustryInterests}
      />
      {renderEditButtons()}
    </div>
  );
};

export { BackgroundTab };
